import { useState, useEffect, type CSSProperties, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import Cookies from 'js-cookie';
import { ROUTE } from '@/routes/constants';
// 文案数据复用「副本」数据模块(繁/英双语 + 品牌改名 + BANNERS),与 /auth/about-v2 页共享
import { ABOUT, BANNERS, IMG_BASE, IMG_V2, type Lang, type Pair } from './about-us-v2.data';
import './about-us.css';

import LogoTc from '@/assets/images/logo.svg';
import LogoEn from '@/assets/images/logo_en.svg';

// 抹字段落背景图目录(crop-about-v3.py 产出的 7 张:hero/system/why/who/reviews/apply/contact)
const IMG_V3 = '/images/about/v3';

// Figma 稿宽 1920;叠加层坐标全部取自 Figma bbox(1920 空间),用 %/cqw 表达以随段宽等比缩放。
const BW = 1920;
const pc = (n: number, d: number) => `${((n / d) * 100).toFixed(4)}%`;
const cq = (fs: number) => `${((fs / BW) * 100).toFixed(4)}cqw`;
// 段内绝对定位叠加:x/y/w 为 Figma 局部坐标(相对本段左上),H=本段 Figma 高度,fs=Figma 字号。
const S = (x: number, y: number, w: number, H: number, fs: number, extra: CSSProperties = {}): CSSProperties => ({
  left: pc(x, BW), top: pc(y, H), width: pc(w, BW), fontSize: cq(fs), ...extra,
});

// 手机/平板竖屏(≤768)走「流式重排」布局(Route B):图/文竖向堆叠、真流式文本,替代桌面的图叠字。
function useIsMobileFlow() {
  const q = '(max-width: 768px)';
  const [m, setM] = useState(() => typeof window !== 'undefined' && window.matchMedia(q).matches);
  useEffect(() => {
    const mq = window.matchMedia(q);
    const on = () => setM(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return m;
}

// 4 个「為什麼選擇」特色对应的裁出插画(顺序同 BANNERS.intro.features:cycle/venn/security/multidevice)
const WHY_IL = ['why-cycle', 'why-triangle', 'why-security', 'why-device'];

export default function AboutUsPage() {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const current = Cookies.get('i18next') || i18n.language || 'zh-HK';
  const lang: Lang = current.startsWith('en') ? 'e' : 't';
  const EN = lang === 'e'; // 英文文案更长:桌面叠加层针对英文缩字号/调间距,以塞进为繁體烤入的定尺寸容器,繁體不动
  const tr = (p: Pair) => (lang === 'e' ? p.e : p.t);
  const setLang = (lng: 'en' | 'zh-HK') => { i18n.changeLanguage(lng); Cookies.set('i18next', lng); };

  const [openFaq, setOpenFaq] = useState<Set<number>>(new Set());
  const [popupOpen, setPopupOpen] = useState(false);
  const mobile = useIsMobileFlow();

  // 品牌词高亮:标题里的「易學平台 / EASE」染橙
  const BRAND = lang === 'e' ? 'EASE' : '易學平台';
  const hb = (s: string): ReactNode =>
    s.split(BRAND).flatMap((seg, i) =>
      i === 0 ? [seg] : [<span key={i} className="v3-brand">{BRAND}</span>, seg]);
  // 角色名:品牌橙 + 角色词(颜色由 color 传入)
  const roleName = (name: Pair, color: string): ReactNode => (
    <>
      <span className="v3-brand">{BRAND}</span>
      <span style={{ color }}>{tr(name).split(BRAND)[1] ?? ''}</span>
    </>
  );

  const toSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView();
  };
  const toggleFaq = (i: number) =>
    setOpenFaq((prev) => {
      const n = new Set(prev);
      if (n.has(i)) n.delete(i); else n.add(i);
      return n;
    });

  // 颜色取自 Figma 逐字色值:Student=lime #d2e186,Teacher=淺藍 #86bbe1,标题正文=纯黑 #000000,说明=灰 #575c60
  const STU = '#d2e186', TEA = '#86bbe1', GRAY = '#575c60', DARK = '#000000';

  return (
    <div className={`about-v3${lang === 'e' ? ' lang-en' : ''}`}>
      {/* ── 固定顶栏 ── */}
      <header className="v3-top">
        <div className="v3-top-in">
          <img className="v3-log" src={lang === 'e' ? LogoEn : LogoTc} alt="易學平台" onClick={() => navigate(ROUTE.LOGIN)} />
          <div className="v3-top-sp" />
          <div className="v3-lang">
            <button className={lang === 'e' ? 'on' : ''} onClick={() => setLang('en')}>English</button>
            <button className={lang === 't' ? 'on' : ''} onClick={() => setLang('zh-HK')}>繁體</button>
          </div>
        </div>
      </header>

      <div className="v3-stage">
        {/* ========== 桌面/平板横屏(>768):图叠字设计,保持不变 ========== */}
        {!mobile && (<>
        {/* ══════════ 1 系統簡介 hero (H=1080) ══════════ */}
        <section id="introducei" className="v3-band">
          <img className="v3-bg" src={`${IMG_V3}/01-hero.png`} alt="" />
          {/* 锚点导航:自适应宽的绿面板,盖住烤入的绿面板(繁体=同尺寸,英文=自动加宽) */}
          <ul className="v3-ov v3-nav" style={{ right: '1.4%', top: '11.5%', fontSize: cq(40), lineHeight: '1.68', padding: `${cq(30)} ${cq(40)}` }}>
            {ABOUT.nav.map((n) => (
              <li key={n.id} onClick={() => toSection(n.id)}>{tr(n.label)}</li>
            ))}
          </ul>
          {/* 标题 + 介绍 */}
          <h1 className="v3-ov" style={S(415, 420, 710, 1080, 72, { textAlign: 'center', fontWeight: 520, color: DARK, letterSpacing: '1px', lineHeight: '1' })}>
            {hb(tr(BANNERS.intro.title))}
          </h1>
          <p className="v3-ov" style={S(256, 533, 1028, 1080, 32, { textAlign: 'center', lineHeight: '1.25', color: GRAY, fontWeight: 450 })}>
            {tr(BANNERS.intro.lead)}
          </p>
        </section>

        {/* ══════════ 2 系統包括 (top=1080 H=1800) ══════════ */}
        <section className="v3-band">
          <img className="v3-bg" src={`${IMG_V3}/02-system.png`} alt="" />
          <h2 className="v3-ov" style={S(280, 311, 720, 1800, 64, { fontWeight: 520, color: DARK, whiteSpace: 'nowrap', lineHeight: '1.12' })}>{hb(tr(BANNERS.intro.systemTitle))}</h2>
          {/* Student(图左文右) */}
          <h3 className="v3-ov" style={S(1153, 493, 560, 1800, 64, { fontWeight: 520, lineHeight: '1.12' })}>{roleName(BANNERS.intro.student.name, STU)}</h3>
          <div className="v3-ov" style={S(1136, 585, EN ? 660 : 620, 1800, EN ? 28 : 40, { fontWeight: 450, color: DARK, lineHeight: EN ? '1.2' : '1' })}>{tr(BANNERS.intro.student.subtitle)}</div>
          <ul className="v3-ov" style={S(1070, EN ? 668 : 662, 700, 1800, EN ? 27 : 40, { listStyle: 'none', color: DARK, lineHeight: '1.2' })}>
            {BANNERS.intro.student.points.map((p, i) => <li key={i}>·{tr(p)}</li>)}
          </ul>
          {/* Teacher(文左图右) */}
          <h3 className="v3-ov" style={S(264, 1187, 560, 1800, 64, { fontWeight: 520, lineHeight: '1.12' })}>{roleName(BANNERS.intro.teacher.name, TEA)}</h3>
          {/* Teacher 副标题:对齐设计图(与 Student 同句「基於課堂錄像的可視化學習系統」);English 无设计参考,沿用 v2 教师专属描述 */}
          <div className="v3-ov" style={S(247, 1279, EN ? 660 : 620, 1800, EN ? 28 : 40, { fontWeight: 450, color: DARK, lineHeight: EN ? '1.2' : '1' })}>{lang === 'e' ? BANNERS.intro.teacher.subtitle.e : '基於課堂錄像的可視化學習系統'}</div>
          <ul className="v3-ov" style={S(198, EN ? 1372 : 1356, 660, 1800, EN ? 27 : 40, { listStyle: 'none', color: DARK, lineHeight: '1.2' })}>
            {BANNERS.intro.teacher.points.map((p, i) => <li key={i}>·{tr(p)}</li>)}
          </ul>
        </section>

        {/* ══════════ 3 為什麼選擇 (top=2880 H=3056) ══════════ */}
        <section className="v3-band">
          <img className="v3-bg" src={`${IMG_V3}/03-why.png`} alt="" />
          <h2 className="v3-ov" style={S(648, 138, 700, 3056, 64, { fontWeight: 520, color: DARK, lineHeight: '1.12' })}>{hb(tr(BANNERS.intro.whyTitle))}</h2>
          {(() => {
            const F = BANNERS.intro.features;
            const pos = [
              { tx: 238, ty: 400, dx: 181, dy: 501, dw: 700 },
              { tx: 1133, ty: 1066, dx: 1070, dy: 1191, dw: 700 },
              { tx: 244, ty: 1762, dx: 181, dy: 1861, dw: 700 },
              { tx: 1133, ty: 2460, dx: 1070, dy: 2591, dw: 700 },
            ];
            // 英文标题较长会占两行(1.5 行高会顶到说明);英文压紧行高并把「双行标题」特色(#3/#4)的说明整体下移,
            // 同时说明字号 40→30 以塞进为繁體烤入的蓝卡(~808×400)。
            const twoLineTitle = new Set([2, 3]); // Your experience… / Enjoy learning…
            // 英文长说明(#2 Collaborate 5 行 / #3 Your experience 6 行)会顶破蓝卡底缘:再缩字号 30→27 + 紧行高 1.15
            const longDesc = new Set([1, 2]);
            return F.map((f, i) => (
              <div key={i}>
                <h3 className="v3-ov" style={S(pos[i].tx, pos[i].ty, 620, 3056, 48, { fontWeight: 520, color: DARK, lineHeight: EN ? '1.18' : '1.5' })}>{tr(f.title)}</h3>
                <p className="v3-ov" style={S(pos[i].dx, EN && twoLineTitle.has(i) ? pos[i].dy + 34 : pos[i].dy, pos[i].dw, 3056, EN ? (longDesc.has(i) ? 27 : 30) : 40, { color: DARK, lineHeight: EN && longDesc.has(i) ? '1.15' : '1.2' })}>{tr(f.desc)}</p>
              </div>
            ));
          })()}
        </section>

        {/* ══════════ 4 誰在使用 (top=5936 H=1080) ══════════ */}
        <section id="introduceu" className="v3-band">
          <img className="v3-bg" src={`${IMG_V3}/04-who.png`} alt="" />
          <h2 className="v3-ov" style={S(0, 172, BW, 1080, 64, { textAlign: 'center', fontWeight: 520, color: DARK, lineHeight: '1.12' })}>{hb(tr(BANNERS.users.whoTitle))}</h2>
          {/* 统计句(大数字内联)。英文整句 + 大数字远超单行宽度:折成两行(每行一个大数字),繁體仍单行不动。 */}
          <p className="v3-ov" style={S(0, EN ? 306 : 430, BW, 1080, 64, { textAlign: 'center', fontWeight: 520, color: DARK, lineHeight: EN ? '6.77cqw' : '120px', whiteSpace: EN ? 'normal' : 'nowrap' })}>
            {tr(BANNERS.users.stat.prefix)}
            <span style={{ fontSize: cq(120), color: '#ff6874', fontWeight: 520, margin: '0 .1em', verticalAlign: '-.12em' }}>{BANNERS.users.stat.n1}</span>
            {tr(BANNERS.users.stat.mid)}
            {EN && <br />}
            <span style={{ fontSize: cq(120), color: '#ff6874', fontWeight: 520, margin: '0 .1em', verticalAlign: '-.12em' }}>{BANNERS.users.stat.n2}</span>
            {tr(BANNERS.users.stat.suffix)}
          </p>
          <p className="v3-ov" style={S(0, 645, BW, 1080, 32, { textAlign: 'center', color: GRAY, lineHeight: '1.25' })}>
            <span style={{ display: 'inline-block', maxWidth: pc(1082, BW) }}>{tr(BANNERS.users.desc)}</span>
          </p>
          <div className="v3-ov" style={S(467, 861, 200, 1080, 32, { color: GRAY, whiteSpace: 'nowrap' })}>{tr(BANNERS.users.fundLabel)}</div>
        </section>

        {/* ══════════ 5 用戶評價 (top=7016 H=1167) ══════════ */}
        <section className="v3-band">
          <img className="v3-bg" src={`${IMG_V3}/05-reviews.png`} alt="" />
          {/* 英文标题较长:缩字号(繁體不动)以塞进烤入白药丸 */}
          <h2 className="v3-ov" style={S(0, 80, BW, 1167, EN ? 55 : 64, { textAlign: 'center', fontWeight: 520, color: DARK, lineHeight: '1.12' })}>{hb(tr(BANNERS.users.reviewTitle))}</h2>
          {(() => {
            const T = BANNERS.users.testimonials;
            // 繁體:引言/作者定位取自设计图气泡内位置。
            const bT = [
              { qx: 124, qy: 365, qw: 343, ax: 147, ay: 546 },
              { qx: 1455, qy: 371, qw: 346, ax: 1480, ay: 532 },
              { qx: 114, qy: 736, qw: 363, ax: 147, ay: 908 },
              { qx: 1455, qy: 718, qw: 346, ax: 1520, ay: 908 },
            ];
            // 英文引言长得多:在烤入气泡(约 423×290,左 x82-505 / 右 x1414-1837)内水平居中、加宽、缩字号,
            // 作者贴气泡下缘,避免引言溢出压到作者。
            const bE = [
              { qx: 103, qy: 358, qw: 380, ax: 103, ay: 536 },
              { qx: 1435, qy: 358, qw: 380, ax: 1435, ay: 536 },
              { qx: 103, qy: 719, qw: 380, ax: 103, ay: 897 },
              { qx: 1435, qy: 719, qw: 380, ax: 1435, ay: 897 },
            ];
            const b = EN ? bE : bT;
            return T.map((r, i) => (
              <div key={i}>
                <p className="v3-ov" style={S(b[i].qx, b[i].qy, b[i].qw, 1167, EN ? 20 : 30, { color: GRAY, lineHeight: EN ? '1.25' : '1.15', textAlign: 'center' })}>{tr(r.quote)}</p>
                {/* 作者:横杠前缀 + 在气泡内水平居中(对照设计图) */}
                <p className="v3-ov" style={S(b[i].qx, b[i].ay, b[i].qw, 1167, EN ? 20 : 26.7, { color: GRAY, textAlign: 'center' })}>— {tr(r.author)}</p>
              </div>
            ));
          })()}
        </section>

        {/* ══════════ 6 帳戶申請 (top=8183 H=1080) ══════════ */}
        <section id="apply" className="v3-band">
          <img className="v3-bg" src={`${IMG_V3}/06-apply.png`} alt="" />
          {/* 英文标题远长于短繁体「如何申請帳戶?」烤入的窄白药丸:大幅缩字号以塞进药丸(繁體不动) */}
          <h2 className="v3-ov" style={S(0, EN ? 152 : 144, BW, 1080, EN ? 36 : 64, { textAlign: 'center', fontWeight: 520, color: DARK })}>{tr(ABOUT.apply.title)}</h2>
          <h3 className="v3-ov" style={S(1134, 462, 720, 1080, 64, { fontWeight: 520, color: DARK, whiteSpace: 'nowrap', lineHeight: '1.12' })}>{hb(tr(ABOUT.apply.t1))}</h3>
          <p className="v3-ov" style={S(967, 570, 834, 1080, 32, { color: GRAY, lineHeight: '1.25' })} dangerouslySetInnerHTML={{ __html: tr(ABOUT.apply.t2) }} />
          {/* 叠加层与烤入的绿色药丸(Rectangle 174:x1212 y729 w345 h99)等框,flex 双向居中 */}
          <div className="v3-ov" style={S(1212, 729, 345, 1080, 40, { height: pc(99, 1080), display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#415111', fontWeight: 520, cursor: 'pointer' })} onClick={() => setPopupOpen(true)}>
            {tr(ABOUT.apply.button)}
          </div>
        </section>

        {/* ══════════ 7 常見問題(复用 v2 结构) ══════════ */}
        <section id="faq" className="v3-faq">
          <div className="h0">{hb(tr(ABOUT.faqTitle))}</div>
          {ABOUT.faq.map((item, i) => {
            const open = openFaq.has(i);
            return (
              <div className="item" key={i}>
                <div className="q" onClick={() => toggleFaq(i)}>
                  <span>{tr(item.q)}</span>
                  <span className="toggleico">{open ? '−' : '+'}</span>
                </div>
                {open && (
                  <div className="a">
                    {item.a.map((para, j) => (
                      <div key={j}>
                        {item.a.length > 1 && <span className="dot" />}
                        <span>{tr(para)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </section>

        {/* ══════════ 8 研究成果(对齐设计图:band #f5f7e2,绿药丸标题左上 + 右偏卡片,card1/3 绿底、card2/4 透明) ══════════ */}
        <section id="link" className="v3-band v3-pubs">
          {(() => {
            const PH = 1990; // 本段 Figma 高度
            // 逐卡定位(Figma 1920 空间):绿卡用其绿底 rect 的 bbox(含内边距),透明卡用文本 bbox
            // 每卡一条通栏 flex 行:左缩略图 + 右文案框,align-items:center 令图与文案模块居中对齐;
            // 行顶取自 Figma 纵向节奏(y),行宽统一(左对齐标题药丸 → 原绿卡右缘 1786)。
            const P = [
              { green: true, y: 373, pt: 37 },
              { green: false, y: 741, pt: 0 },
              { green: true, y: 1100, pt: 34 },
              { green: false, y: 1550, pt: 0 },
            ];
            const ROW_X = 133;   // 与标题药丸左对齐
            const ROW_W = 1653;  // 通栏宽:左缩略图列 → 右文案框(至原绿卡右缘 1786)
            const THUMB_W = 560; // 放大后的缩略图宽(Figma),高随图比自适应
            const GAP = 64;      // 图↔文案框间距
            return (
              <>
                {/* 绿药丸标题(左上) */}
                <div className="v3-ov v3-pubs-title" style={S(133, 132, 403, PH, 64, { height: pc(131, PH), display: 'flex', alignItems: 'center', justifyContent: 'center' })}>
                  {tr(ABOUT.publicationsTitle)}
                </div>
                {ABOUT.publications.map((p, i) => {
                  const c = P[i];
                  const boxPad = c.green
                    ? { paddingTop: cq(c.pt), paddingBottom: cq(c.pt), paddingLeft: cq(65), paddingRight: cq(65) }
                    : {};
                  return (
                    <div
                      key={i}
                      className="v3-ov v3-pub-row"
                      style={{ left: pc(ROW_X, BW), top: pc(c.y, PH), width: pc(ROW_W, BW), display: 'flex', alignItems: 'center', columnGap: cq(GAP) }}
                    >
                      <img className="v3-pub-thumb" src={`${IMG_BASE}/${p.img}.png`} alt="" style={{ width: pc(THUMB_W, BW) }} />
                      <div className={`v3-pub${c.green ? ' green' : ''}`} style={{ flex: 1, minWidth: 0, fontSize: cq(32), ...boxPad }}>
                        {p.title}<br />{p.authors}{p.journal ? (<><br />{p.journal}</>) : null}
                        {p.link && (<><br /><a target="_blank" rel="noreferrer" href={p.link}>{p.link}</a></>)}
                      </div>
                    </div>
                  );
                })}
              </>
            );
          })()}
        </section>

        {/* ══════════ 9 團隊成員(复用 v2 结构) ══════════ */}
        <section id="member" className="v3-members">
          <div className="title">
            <span className="red">{tr(ABOUT.membersTitle.brand)}</span>
            <span>{tr(ABOUT.membersTitle.suffix)}</span>
          </div>
          <ul>
            {ABOUT.members.map((m, i) => (
              <li key={i}>
                <div className="avatarwrap" style={m.bg ? { background: m.bg } : undefined}>
                  <img src={`${IMG_BASE}/photo/${m.photo}.png`} alt={tr(m.name)} />
                </div>
                <div className="b">{tr(m.name)}</div>
                <div className="t">
                  <div>{tr(ABOUT.orgLabels[m.org])}</div>
                  <div>{tr(ABOUT.orgLabels.lab)}</div>
                  <div>{tr(m.role)}</div>
                </div>
              </li>
            ))}
          </ul>
        </section>

        {/* ══════════ 10 聯絡我們 (top=16875 H=1187) ══════════ */}
        <section id="introduceb" className="v3-band">
          <img className="v3-bg" src={`${IMG_V3}/10-contact.png`} alt="" />
          {/* 英文「Contact Us」:字号轻缩 + 把文字盒撑成黄药丸实测尺寸(中心 334 / 宽 401,像素采样得)并居中,
              使其在药丸内水平垂直都居中(繁體左对齐布局不动) */}
          <h2 className="v3-ov" style={S(EN ? 133 : 209, EN ? 169 : 150, EN ? 402 : 400, 1187, EN ? 58 : 64, { fontWeight: 520, color: DARK, lineHeight: '1.12', ...(EN ? { textAlign: 'center' as const } : {}) })}>{tr(BANNERS.contact.title)}</h2>
          {/* 三项与烤入的 📍✉️☎ 图标(约 100px 节奏)对齐:繁體地址单行天然对齐;英文地址长会折 2 行,
              故英文给每项固定 100px 槽 + 垂直居中并缩字号,让 email/phone 仍与 ✉/☎ 对齐,不被地址顶下去 */}
          <div className="v3-ov" style={S(285, 352, 700, 1187, EN ? 30 : 40, { color: DARK, lineHeight: EN ? '1.15' : '2.5' })}>
            {EN ? (
              [tr(BANNERS.contact.address), BANNERS.contact.email, BANNERS.contact.phone].map((t, i) => (
                <div key={i} style={{ height: cq(100), display: 'flex', alignItems: 'center' }}>{t}</div>
              ))
            ) : (
              <>
                <div>{tr(BANNERS.contact.address)}</div>
                <div>{BANNERS.contact.email}</div>
                <div>{BANNERS.contact.phone}</div>
              </>
            )}
          </div>
        </section>
        </>)}

        {/* ========== 手机/平板竖屏(≤768):流式重排(参照源站),图/文竖向堆叠、真流式文本 ========== */}
        {mobile && (
          <div className="v3-m">
            {/* 1 系統簡介 */}
            <section id="introducei" className="v3-m-sec v3-m-hero v3-m-center">
              <h1 className="v3-m-h1">{hb(tr(BANNERS.intro.title))}</h1>
              <p className="v3-m-lead">{tr(BANNERS.intro.lead)}</p>
            </section>

            {/* 2 系統包括 */}
            <section className="v3-m-sec v3-m-peach">
              <h2 className="v3-m-title">{hb(tr(BANNERS.intro.systemTitle))}</h2>
              <img className="v3-m-il" src={`${IMG_V3}/il/student.png`} alt="" />
              <div className="v3-m-card">
                <h3 className="v3-m-role">{roleName(BANNERS.intro.student.name, STU)}</h3>
                <div className="v3-m-sub">{tr(BANNERS.intro.student.subtitle)}</div>
                <ul className="v3-m-points">
                  {BANNERS.intro.student.points.map((p, i) => <li key={i}>{tr(p)}</li>)}
                </ul>
              </div>
              <img className="v3-m-il" src={`${IMG_V3}/il/teacher.png`} alt="" />
              <div className="v3-m-card">
                <h3 className="v3-m-role">{roleName(BANNERS.intro.teacher.name, TEA)}</h3>
                <div className="v3-m-sub">{lang === 'e' ? BANNERS.intro.teacher.subtitle.e : '基於課堂錄像的可視化學習系統'}</div>
                <ul className="v3-m-points">
                  {BANNERS.intro.teacher.points.map((p, i) => <li key={i}>{tr(p)}</li>)}
                </ul>
              </div>
            </section>

            {/* 3 為什麼選擇 */}
            <section className="v3-m-sec v3-m-blue">
              <h2 className="v3-m-title">{hb(tr(BANNERS.intro.whyTitle))}</h2>
              {BANNERS.intro.features.map((f, i) => (
                <div className="v3-m-feat" key={i}>
                  <img className="v3-m-il" src={`${IMG_V3}/il/${WHY_IL[i]}.png`} alt="" />
                  <div className="v3-m-card">
                    <h3 className="v3-m-role">{tr(f.title)}</h3>
                    <p className="v3-m-desc">{tr(f.desc)}</p>
                  </div>
                </div>
              ))}
            </section>

            {/* 4 誰在使用 */}
            <section id="introduceu" className="v3-m-sec v3-m-peach v3-m-center">
              <h2 className="v3-m-title">{hb(tr(BANNERS.users.whoTitle))}</h2>
              <p className="v3-m-stat">
                {tr(BANNERS.users.stat.prefix)}
                <b>{BANNERS.users.stat.n1}</b>{tr(BANNERS.users.stat.mid)}<b>{BANNERS.users.stat.n2}</b>{tr(BANNERS.users.stat.suffix)}
              </p>
              <p className="v3-m-desc">{tr(BANNERS.users.desc)}</p>
              <div className="v3-m-funds">
                <span>{tr(BANNERS.users.fundLabel)}</span>
                {BANNERS.users.funds.map((f, i) => <img key={i} src={`${IMG_V2}/${f}.png`} alt="" />)}
              </div>
            </section>

            {/* 5 用戶評價 */}
            <section className="v3-m-sec v3-m-cream">
              <h2 className="v3-m-title">{hb(tr(BANNERS.users.reviewTitle))}</h2>
              <img className="v3-m-il" src={`${IMG_V3}/il/reviews-phones.png`} alt="" />
              {BANNERS.users.testimonials.map((r, i) => (
                <div className="v3-m-card v3-m-quote" key={i}>
                  <p>{tr(r.quote)}</p>
                  <p className="v3-m-author">— {tr(r.author)}</p>
                </div>
              ))}
            </section>

            {/* 6 帳戶申請 */}
            <section id="apply" className="v3-m-sec v3-m-green v3-m-center">
              <h2 className="v3-m-title">{tr(ABOUT.apply.title)}</h2>
              <h3 className="v3-m-sub2">{hb(tr(ABOUT.apply.t1))}</h3>
              <p className="v3-m-desc" dangerouslySetInnerHTML={{ __html: tr(ABOUT.apply.t2) }} />
              <button className="v3-m-btn" type="button" onClick={() => setPopupOpen(true)}>{tr(ABOUT.apply.button)}</button>
            </section>

            {/* 7 常見問題(复用 .v3-faq 结构,本就自适应) */}
            <section id="faq" className="v3-faq">
              <div className="h0">{hb(tr(ABOUT.faqTitle))}</div>
              {ABOUT.faq.map((item, i) => {
                const open = openFaq.has(i);
                return (
                  <div className="item" key={i}>
                    <div className="q" onClick={() => toggleFaq(i)}>
                      <span>{tr(item.q)}</span>
                      <span className="toggleico">{open ? '−' : '+'}</span>
                    </div>
                    {open && (
                      <div className="a">
                        {item.a.map((para, j) => (
                          <div key={j}>
                            {item.a.length > 1 && <span className="dot" />}
                            <span>{tr(para)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </section>

            {/* 8 研究成果(流式:绿药丸标题 + 竖排卡片,green/透明交替) */}
            <section id="link" className="v3-m-sec v3-m-pubs">
              <div className="v3-m-pill">{tr(ABOUT.publicationsTitle)}</div>
              {ABOUT.publications.map((p, i) => (
                <div key={i} className={`v3-m-pub${i % 2 === 0 ? ' green' : ''}`}>
                  <img className="v3-m-pub-img" src={`${IMG_BASE}/${p.img}.png`} alt="" />
                  {p.title}<br />{p.authors}{p.journal ? (<><br />{p.journal}</>) : null}
                  {p.link && (<><br /><a target="_blank" rel="noreferrer" href={p.link}>{p.link}</a></>)}
                </div>
              ))}
            </section>

            {/* 9 團隊成員(复用 .v3-members 结构) */}
            <section id="member" className="v3-members">
              <div className="title">
                <span className="red">{tr(ABOUT.membersTitle.brand)}</span>
                <span>{tr(ABOUT.membersTitle.suffix)}</span>
              </div>
              <ul>
                {ABOUT.members.map((m, i) => (
                  <li key={i}>
                    <div className="avatarwrap" style={m.bg ? { background: m.bg } : undefined}>
                      <img src={`${IMG_BASE}/photo/${m.photo}.png`} alt={tr(m.name)} />
                    </div>
                    <div className="b">{tr(m.name)}</div>
                    <div className="t">
                      <div>{tr(ABOUT.orgLabels[m.org])}</div>
                      <div>{tr(ABOUT.orgLabels.lab)}</div>
                      <div>{tr(m.role)}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </section>

            {/* 10 聯絡我們 */}
            <section id="introduceb" className="v3-m-sec v3-m-peach v3-m-center">
              <h2 className="v3-m-title">{tr(BANNERS.contact.title)}</h2>
              <img className="v3-m-il" src={`${IMG_V3}/il/contact-people.png`} alt="" />
              <div className="v3-m-contact">
                <div>{tr(BANNERS.contact.address)}</div>
                <div>{BANNERS.contact.email}</div>
                <div>{BANNERS.contact.phone}</div>
              </div>
            </section>
          </div>
        )}
      </div>

      {/* ── 申请表弹窗(1:1 复用 v2) ── */}
      {popupOpen && (
        <>
          <div className="popup-mask" onClick={() => setPopupOpen(false)} />
          <div className="popup">
            <img className="close" src={`${IMG_BASE}/close.svg`} alt="close" onClick={() => setPopupOpen(false)} />
            <div className="title">{tr(ABOUT.form.title)}</div>
            <div className="items">
              <div className="item"><div className="h3">{tr(ABOUT.form.fields.firstname)}</div><input type="text" /></div>
              <div className="item"><div className="h3">{tr(ABOUT.form.fields.lastname)}</div><input type="text" /></div>
              <div className="item"><div className="h3">{tr(ABOUT.form.fields.email)}</div><input type="text" /></div>
              <div className="item"><div className="h3">{tr(ABOUT.form.fields.phone)}</div><input type="text" /></div>
              <div className="item"><div className="h3">{tr(ABOUT.form.fields.depart)}</div><input type="text" /></div>
              <div className="item">
                <div className="h3">{tr(ABOUT.form.fields.departtype)}</div>
                <select defaultValue="">
                  {ABOUT.form.options.map((o, i) => (<option key={i} value={i === 0 ? '' : i - 1}>{tr(o)}</option>))}
                </select>
              </div>
              <div className="item"><div className="h3">{tr(ABOUT.form.fields.subject)}</div><input type="text" /></div>
              <div className="item"><div className="h3">{tr(ABOUT.form.fields.grade)}</div><input type="text" /></div>
              <div className="item fullitem"><div className="h3">{tr(ABOUT.form.fields.remark)}</div><textarea rows={4} /></div>
              <div className="tip">{tr(ABOUT.form.tip)}</div>
              <div className="btnbar"><button className="btn" type="button" disabled>{tr(ABOUT.form.submit)}</button></div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
